import React, { RefObject, useEffect, useRef, useState } from 'react';
import { observe } from 'react-intersection-observer';
import { srOnly } from '@domain-group/fe-brary';
import { Button, LinkButton } from '@domain-group/fe-blueprint-ui/button';
import {
  EmailIcon,
  PhoneIcon,
  AgentFilledIcon,
} from '@domain-group/fe-co-icon';
import Avatar, { isNoAgentPhoto } from '../shared/avatar';
import {
  addAusCodeToPhoneNumber,
  formatTelephoneNumber,
  getAppraisalCTAUrl,
} from '../../utils';
import { COMPONENT_IDS } from '../../constants';
import * as styles from './contact-card-2026.style';

export type ContactCardVariant =
  | 'appraisal'
  | 'propertyManager'
  | 'administrator';

export interface ContactCard2026Props {
  variant: ContactCardVariant;
  name: string;
  jobTitle?: string | null;
  profilePhoto?: string | null;
  telephone?: string | null;
  hasEmail?: boolean | null;
  hideAppraisalCta?: boolean;
  requestAppraisalUrl?: string;
  contactId?: number;
  // The hero avatar/photo markup is different per breakpoint (NAW-271) -
  // pass every hero element currently mounted, whichever is visible for the
  // current viewport width drives the reveal, the rest report as not
  // intersecting on their own (zero-size when CSS-hidden).
  heroAvatarRefs?: Array<RefObject<Element> | null | undefined>;
  onGetAppraisal?: () => void;
  onOpenEnquiryForm?: () => void;
  onCall?: () => void;
  onCallReveal?: () => void;
}

type ButtonAppearance = Parameters<typeof Button>[0]['appearance'];

const getFirstName = (name: string): string => name.trim().split(' ')[0];

const isDigit = (char: string): boolean => char >= '0' && char <= '9';

const maskPhoneNumberTail = (formattedNumber: string): string => {
  let end = formattedNumber.length;
  while (end > 0 && isDigit(formattedNumber[end - 1])) {
    end -= 1;
  }

  return (
    formattedNumber.slice(0, end) + '*'.repeat(formattedNumber.length - end)
  );
};

const useHeroInView = (
  heroAvatarRefs: Array<RefObject<Element> | null | undefined> = [],
): boolean => {
  const [heroInView, setHeroInView] = useState(true);

  useEffect(() => {
    const elements = heroAvatarRefs
      .map((ref) => ref?.current)
      .filter((element): element is Element => Boolean(element));

    if (elements.length === 0) {
      // No hero avatar to observe (refs omitted, or none mounted yet) -
      // nothing to scroll away from, so don't leave the identity
      // permanently collapsed waiting for an observer that will never fire.
      setHeroInView(false);
      return undefined;
    }

    const visible = new Set<Element>();
    const unobserveFns = elements.map((element) =>
      observe(element, (inView) => {
        if (inView) {
          visible.add(element);
        } else {
          visible.delete(element);
        }
        setHeroInView(visible.size > 0);
      }),
    );

    return () => unobserveFns.forEach((unobserve) => unobserve());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are stable useRef objects
  }, heroAvatarRefs);

  return heroInView;
};

const AgentIdentity = ({
  name,
  jobTitle,
  profilePhoto,
  revealed,
  showJobTitle,
}: {
  name: string;
  jobTitle?: string | null;
  profilePhoto?: string | null;
  revealed: boolean;
  showJobTitle: boolean;
}): JSX.Element => (
  <div css={styles.identity(revealed)}>
    {profilePhoto && !isNoAgentPhoto(profilePhoto) ? (
      <div
        css={styles.avatarWrapper(revealed)}
        data-testid="contact-card-2026__avatar"
        data-revealed={revealed}
      >
        <Avatar
          css={styles.avatarImage(revealed)}
          imageUrl={profilePhoto}
          imageTitle={name}
          shape="square"
          size="custom"
        />
      </div>
    ) : (
      <div
        css={styles.avatarPlaceholder(revealed)}
        data-testid="contact-card-2026__avatar-placeholder"
        data-revealed={revealed}
      >
        <AgentFilledIcon
          viewBox="0 0 18 20"
          css={styles.avatarPlaceholderIcon}
        />
      </div>
    )}
    <div css={styles.identityText}>
      <h2 css={styles.heading}>Contact {getFirstName(name)}</h2>
      {showJobTitle && (
        <div
          css={styles.jobTitleWrapper(revealed)}
          data-testid="contact-card-2026__job-title-wrapper"
          data-revealed={revealed}
        >
          <span css={styles.jobTitle}>{jobTitle}</span>
        </div>
      )}
    </div>
  </div>
);

export const PhoneNumberReveal = ({
  telephone,
  name,
  maskedLabel,
  appearance = 'ghostPrimary',
  hideLabelOnMobile = false,
  onCall,
  onCallReveal,
}: {
  telephone: string;
  name: string;
  // Sidebar/desktop shows the masked number as the label; the tablet
  // floating bar shows "Call" instead, revealing inline the same way.
  maskedLabel?: string;
  appearance?: ButtonAppearance;
  // Enquiry modal header CTA only - drops to icon-only on mobile.
  hideLabelOnMobile?: boolean;
  onCall?: () => void;
  onCallReveal?: () => void;
}): JSX.Element => {
  const [isRevealed, setIsRevealed] = useState(false);
  const revealedLinkRef = useRef<HTMLAnchorElement>(null);
  const internationalNumber = addAusCodeToPhoneNumber(telephone);
  const formattedNumber = formatTelephoneNumber(internationalNumber);

  useEffect(() => {
    if (isRevealed) {
      // Focus the revealed anchor after re-render, so keyboard users don't
      // lose their position when the masked button is replaced.
      revealedLinkRef.current?.focus();
    }
  }, [isRevealed]);

  const reveal = () => {
    setIsRevealed(true);
    onCallReveal?.();
  };

  const button = isRevealed ? (
    <LinkButton
      href={`tel:${internationalNumber}`}
      ref={revealedLinkRef}
      appearance={appearance}
      icon={<PhoneIcon name="Phone" size="regular" />}
      css={styles.button}
      onClick={() => onCall?.()}
      data-testid="contact-card-2026__phone-revealed"
    >
      <span css={srOnly}>Call {name} on </span>
      {formattedNumber}
    </LinkButton>
  ) : (
    <Button
      type="button"
      appearance={appearance}
      icon={<PhoneIcon name="Phone" size="regular" />}
      css={styles.button}
      data-testid="contact-card-2026__phone-masked"
      onClick={reveal}
      onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter') {
          reveal();
        }
      }}
    >
      <span css={srOnly}>Reveal phone number for </span>
      {maskedLabel ?? maskPhoneNumberTail(formattedNumber)}
      <span css={srOnly}> {name}</span>
    </Button>
  );

  if (!hideLabelOnMobile) {
    return button;
  }

  const iconOnlyButton = isRevealed ? (
    <LinkButton
      href={`tel:${internationalNumber}`}
      appearance={appearance}
      icon={<PhoneIcon name="Phone" size="regular" />}
      css={styles.button}
      onClick={() => onCall?.()}
      aria-label={`Call ${name} on ${formattedNumber}`}
      data-testid="contact-card-2026__phone-revealed-icon-only"
    />
  ) : (
    <Button
      type="button"
      appearance={appearance}
      icon={<PhoneIcon name="Phone" size="regular" />}
      css={styles.button}
      data-testid="contact-card-2026__phone-masked-icon-only"
      onClick={reveal}
      onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter') {
          reveal();
        }
      }}
      aria-label={`Reveal phone number for ${name}`}
    />
  );

  return (
    <>
      <span css={styles.labelledButtonWrapper}>{button}</span>
      <span css={styles.iconOnlyButtonWrapper}>{iconOnlyButton}</span>
    </>
  );
};

const ContactCard2026 = ({
  variant,
  name,
  jobTitle,
  profilePhoto,
  telephone,
  hasEmail,
  hideAppraisalCta = false,
  requestAppraisalUrl,
  contactId,
  heroAvatarRefs,
  onGetAppraisal,
  onOpenEnquiryForm,
  onCall,
  onCallReveal,
}: ContactCard2026Props): JSX.Element => {
  const heroInView = useHeroInView(heroAvatarRefs);
  const revealed = !heroInView;
  const isAppraisalVariant = variant === 'appraisal';
  const enquiryFormHref = `#${COMPONENT_IDS.enquiryForm}`;

  const handleOpenEnquiryForm = (event: React.MouseEvent): void => {
    if (onOpenEnquiryForm) {
      event.preventDefault();
      onOpenEnquiryForm();
    }
  };
  const appraisalHref = getAppraisalCTAUrl({
    url: requestAppraisalUrl,
    agentId: contactId ? String(contactId) : undefined,
    entryPoint: 'Contact Card',
  });

  const showEmailCta = Boolean(hasEmail);
  const showAppraisalCta =
    isAppraisalVariant && !hideAppraisalCta && showEmailCta;

  const primaryCta = showAppraisalCta ? (
    <LinkButton
      href={appraisalHref}
      css={styles.button}
      data-testid="contact-card-2026__cta-appraisal"
      onClick={() => onGetAppraisal?.()}
    >
      Request a free appraisal
    </LinkButton>
  ) : (
    !isAppraisalVariant &&
    showEmailCta && (
      <LinkButton
        href={enquiryFormHref}
        icon={<EmailIcon name="email" size="regular" />}
        css={styles.button}
        data-testid="contact-card-2026__cta-enquire"
        onClick={handleOpenEnquiryForm}
      >
        Enquire
      </LinkButton>
    )
  );

  const secondaryCta = isAppraisalVariant
    ? showEmailCta && (
        <LinkButton
          href={enquiryFormHref}
          appearance="ghostPrimary"
          css={styles.button}
          data-testid="contact-card-2026__cta-contact"
          onClick={handleOpenEnquiryForm}
        >
          Contact
        </LinkButton>
      )
    : telephone && (
        <PhoneNumberReveal
          telephone={telephone}
          name={name}
          onCall={onCall}
          onCallReveal={onCallReveal}
        />
      );

  const primaryCtaFloating = showAppraisalCta ? (
    <LinkButton
      href={appraisalHref}
      css={styles.button}
      data-testid="contact-card-2026__cta-appraisal-floating"
      onClick={() => onGetAppraisal?.()}
    >
      Request appraisal
    </LinkButton>
  ) : (
    !isAppraisalVariant &&
    showEmailCta && (
      <LinkButton
        href={enquiryFormHref}
        icon={<EmailIcon name="email" size="regular" />}
        css={styles.button}
        data-testid="contact-card-2026__cta-enquire-floating"
        onClick={handleOpenEnquiryForm}
      >
        Enquire
      </LinkButton>
    )
  );

  const secondaryCtaFloatingTablet = isAppraisalVariant
    ? showEmailCta && (
        <LinkButton
          href={enquiryFormHref}
          appearance="ghostPrimary"
          css={styles.button}
          data-testid="contact-card-2026__cta-contact-floating"
          onClick={handleOpenEnquiryForm}
        >
          Contact
        </LinkButton>
      )
    : telephone && (
        <PhoneNumberReveal
          telephone={telephone}
          name={name}
          maskedLabel="Call"
          onCall={onCall}
          onCallReveal={onCallReveal}
        />
      );

  const secondaryCtaFloatingMobile = isAppraisalVariant
    ? showEmailCta && (
        <LinkButton
          href={enquiryFormHref}
          appearance="ghostPrimary"
          css={styles.button}
          data-testid="contact-card-2026__cta-contact-floating-mobile"
          onClick={handleOpenEnquiryForm}
        >
          Contact
        </LinkButton>
      )
    : telephone && (
        <LinkButton
          href={`tel:${addAusCodeToPhoneNumber(telephone)}`}
          appearance="ghostPrimary"
          icon={<PhoneIcon name="Phone" size="regular" />}
          css={styles.button}
          data-testid="contact-card-2026__cta-call-mobile"
          onClick={() => onCall?.()}
        >
          Call
        </LinkButton>
      );

  return (
    <>
      {/* Desktop - sticky card in the right column */}
      <div css={styles.sidebarCard} data-testid="contact-card-2026__sidebar">
        <AgentIdentity
          name={name}
          jobTitle={jobTitle}
          profilePhoto={profilePhoto}
          revealed={revealed}
          showJobTitle
        />
        <div css={styles.ctaGroup}>
          {primaryCta}
          {secondaryCta}
        </div>
      </div>

      {/* Mobile - bottom sheet, no identity */}
      {(secondaryCtaFloatingMobile || primaryCtaFloating) && (
        <div
          css={styles.floatingBarMobileOuter}
          data-testid="contact-card-2026__floating-bar-mobile"
        >
          <div css={styles.floatingBarMobileInner}>
            <div css={styles.floatingBarMobileCtaGroup}>
              {secondaryCtaFloatingMobile}
              {primaryCtaFloating}
            </div>
          </div>
        </div>
      )}

      {/* Tablet - detail area + buttons, centred floating card */}
      <div
        css={styles.floatingBarTablet}
        data-testid="contact-card-2026__floating-bar-tablet"
      >
        <div css={styles.floatingBarTabletCard}>
          <AgentIdentity
            name={name}
            jobTitle={jobTitle}
            profilePhoto={profilePhoto}
            revealed={revealed}
            showJobTitle
          />
          <div css={styles.floatingBarCtaGroup}>
            {secondaryCtaFloatingTablet}
            {primaryCtaFloating}
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactCard2026;
